import { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Slider } from 'primereact/slider';
import { InputNumber } from 'primereact/inputnumber';
import UserDropdown from '../UserDropdown';
import { useGetUserQuery } from "../../services/usersApi";
import { useGetTaskLogsQuery, useLazyGetTaskDetailsQuery } from '../../services/tasksApi';
import { useToast } from "../../hooks/useToast.ts";
import TasksDropdown from '../tasksDropdown.js';
import { Divider } from 'primereact/divider';
import LegendColorPicker from "../LegendColorPicker.js";
import { TabView, TabPanel } from 'primereact/tabview';

// Field name dictionary for task log display
const FIELD_LABELS = {
    id: 'ID',
    name: 'Nazwa',
    start: 'Data rozpoczęcia',
    end: 'Data zakończenia',
    progress: 'Progres',
    type: 'Typ',
    hideChildren: 'Ukryj dzieci',
    displayOrder: 'Kolejność wyświetlania',
    dependencies: 'Zależności',
    users: 'Użytkownicy',
    project: 'Projekt',
    topic: 'Temat',
    institution: 'Instytucja',
    workHours: 'Roboczogodziny',
    description: 'Opis',
    bgcolor: 'Kolor tła',
    reminders: 'Powiadomienia'
};

const getFieldLabel = (fieldName) => FIELD_LABELS[fieldName] || fieldName;

const TaskModal = ({
    task,
    visible,
    onHide,
    onSubmit,
    projects = [],
    tasks = [],
    usersData = [],
    institutionsData = [],
}) => {
    const [editedTask, setEditedTask] = useState(task || {
        name: '',
        description: '',
        progress: 0,
        user: null
    });
    const [dependencyOffset, setDependencyOffset] = useState(0);
    // logs are loaded via RTK Query
    const { data: logs = [], isLoading: logsLoading } = useGetTaskLogsQuery({ id: task?.id, limit: 200 }, { skip: !task?.id || !visible, refetchOnMountOrArgChange: true });
    const [getTaskDetails] = useLazyGetTaskDetailsQuery();

    const { data: user, isLoading } = useGetUserQuery();
    const toast = useToast();

    useEffect(() => {
        if (task) {
            // if task.user or task.branch not provided, default to 'null'
            const updatedTask = {
                ...task,
                users: task.users ? task.users : 'null',
                branch: task.branch ? task.branch : 'null'
            };
            setEditedTask(updatedTask);
            
            // Calculate offset from parent task if dependency exists
            if (task.dependencies && task.dependencies.length > 0 && task.dependencies[0] !== "brak") {
                const parentTaskId = task.dependencies[0];
                const parentTask = tasks.find(t => t.id === parentTaskId);
                
                if (parentTask && task.start && parentTask.end) {
                    const parentEndTime = new Date(parentTask.end).setHours(0, 0, 0, 0);
                    const taskStartTime = new Date(task.start).setHours(0, 0, 0, 0);
                    const daysDiff = Math.round((taskStartTime - parentEndTime) / (1000 * 60 * 60 * 24));
                    setDependencyOffset(daysDiff);
                } else {
                    setDependencyOffset(0);
                }
            } else {
                setDependencyOffset(0);
            }
        }
    }, [task, tasks]);

    useEffect(() => {
        if (!visible || !task) return;

        const taskId = task._id || task.id;
        if (!taskId) return;
        const fallbackStart = task.start;
        const fallbackEnd = task.end;

        let cancelled = false;

        getTaskDetails(taskId)
            .unwrap()
            .then((fullTask) => {
                if (cancelled || !fullTask) return;

                const start = fullTask.start ? new Date(fullTask.start) : fallbackStart;
                const end = fullTask.end ? new Date(fullTask.end) : fallbackEnd;

                setEditedTask({
                    ...fullTask,
                    start,
                    end,
                    users: fullTask.users ? fullTask.users : 'null',
                    branch: fullTask.branch ? fullTask.branch : 'null'
                });
            })
            .catch((err) => {
                console.error('Failed to load full task details:', err);
            });

        return () => {
            cancelled = true;
        };
    }, [visible, task?._id, task?.id, task?.start, task?.end, getTaskDetails]);

    // RTK Query hook `useGetTaskLogsQuery` will handle fetching and caching

    const computeChanges = (before, after) => {
        if (!before && after) return [{ key: '(created)', before: null, after }];
        if (before && !after) return [{ key: '(deleted)', before, after: null }];

        const keys = Array.from(new Set([...(before ? Object.keys(before) : []), ...(after ? Object.keys(after) : [])]));
        const changes = [];
        for (const key of keys) {
            const valA = before ? before[key] : undefined;
            const valB = after ? after[key] : undefined;
            // Use JSON.stringify for deep equality check
            const aStr = typeof valA === 'object' ? JSON.stringify(valA) : String(valA === undefined ? '' : valA);
            const bStr = typeof valB === 'object' ? JSON.stringify(valB) : String(valB === undefined ? '' : valB);
            if (aStr !== bStr) {
                changes.push({ key, before: valA, after: valB });
            }
        }
        return changes;
    };

    const handleInputChange = (e) => {
        let field;
        let value;

        if (e.target) {
            field = e.target.name || e.target.id;
            if (e.target.type === "checkbox") value = e.target.checked;
            else value = e.target.value;
        } else {
            field = e.originalEvent?.target?.id || e.originalEvent?.target?.name;
            value = e.value;
        }

        if (!field) return;

        setEditedTask((prev) => {
            const updatedTask = { ...prev, [field]: value };

            // Walidacja pól daty
            const startDate = field === "start" ? value : updatedTask.start;
            const endDate = field === "end" ? value : updatedTask.end;

            // Jeśli oba są ustawione — waliduj
            if (startDate && endDate) {
                const startTime = new Date(startDate).getTime();
                const endTime = new Date(endDate).getTime();

                if (endTime < startTime) {
                    // Automatyczna korekta — przesuwamy drugą datę
                    if (field === "start") {
                        updatedTask.end = value;
                    } else {
                        updatedTask.start = value;
                    }
                }
            }

            return updatedTask;
        });
    };

    const handleUserChange = (id) => {
        const institutionVar = (usersData || []).filter(u => u._id === id).length <= 0;
        setEditedTask({ ...editedTask, users: id.value, institution: institutionVar });
    };

    const handleSubmit = () => {
        // Walidacja nazwy zadania
        if (!editedTask.name || editedTask.name.trim() === '') {
            toast("Nazwa zadania jest wymagana", "error");
            return;
        }
        
        if (editedTask.user === 'null') { editedTask.user = null; }
        if (editedTask.branch === 'null') { editedTask.branch = null; }
        onSubmit(editedTask);
        onHide();
    };
    const handleDependencyChange = (dependency) => {
        let newDependency = []
        if (dependency.id !== "brak") {
            newDependency = [dependency.id]
        }

        setEditedTask({ ...editedTask, dependencies: newDependency })
    }
    
    const handleOffsetChange = (newOffset) => {
        setDependencyOffset(newOffset);
        
        // Update task dates based on parent task and new offset
        if (editedTask.dependencies && editedTask.dependencies.length > 0) {
            const parentTaskId = editedTask.dependencies[0];
            const parentTask = tasks.find(t => t.id === parentTaskId);
            
            if (parentTask && parentTask.end) {
                const parentEndDate = new Date(parentTask.end);
                const taskDuration = editedTask.end && editedTask.start 
                    ? new Date(editedTask.end).getTime() - new Date(editedTask.start).getTime()
                    : 24 * 60 * 60 * 1000; // default 1 day
                
                const newStartDate = new Date(parentEndDate);
                newStartDate.setDate(newStartDate.getDate() + newOffset);
                
                const newEndDate = new Date(newStartDate.getTime() + taskDuration);
                
                setEditedTask({
                    ...editedTask,
                    start: newStartDate,
                    end: newEndDate
                });
            }
        }
    }

    const footer = (
        <div>
            {!isLoading && user && (
                (user._id === editedTask.user) || 
                user.type === "admin" || 
                (user.type === "manager" && projects.find(p => p.id === editedTask.project)?.coordinators?.includes(user._id))
            ) && (
                <Button
                    label="Zapisz"
                    icon="pi pi-check"
                    onClick={handleSubmit}
                />
            )}
            <Button
                label="Anuluj"
                icon="pi pi-times"
                onClick={onHide}
                className="p-button-secondary"
            />
        </div>
    );

    return (
        <Dialog
            header={`Edycja zadania: ${editedTask.name}`}
            visible={visible}
            style={{ width: '50vw' }}
            onHide={onHide}
            footer={footer}
            breakpoints={{
                '960px': '40vw',
                '640px': '60vw'
            }}
        >
            <TabView>
                <TabPanel header="Szczegóły">
                    <div className="p-fluid grid">
                <div className="col-12 md:col-6">
                    <div className="p-field mb-2">
                        <label htmlFor="name">Nazwa zadania <span style={{ color: 'red' }}>*</span></label>
                        <InputText
                            disabled={!(!isLoading && user && (
                                user.type === "admin" || 
                                (user.type === "manager" && projects.find(p => p.id === editedTask.project)?.coordinators?.includes(user._id))
                            ))}
                            id="name"
                            value={editedTask.name}
                            onChange={handleInputChange}
                            required
                            autoFocus
                            placeholder="Wprowadź nazwę zadania"
                        />
                    </div>

                    <div className="p-field mb-2">
                        <label htmlFor="description">Opis zadania</label>
                        <InputTextarea
                            disabled={!(!isLoading && user && (
                                user.type === "admin" || 
                                (user.type === "manager" && projects.find(p => p.id === editedTask.project)?.coordinators?.includes(user._id))
                            ))}
                            id="description"
                            value={editedTask.description}
                            onChange={handleInputChange}
                            rows={5}
                            autoResize
                        />
                    </div>
                    <Divider align="left">
                        <span className="p-tag">Parametry czasowe</span>
                    </Divider>
                    <div className="p-fluid grid align-items-center">
                        <div className="col-6 p-field mb-2">
                            <label htmlFor="hours">Poczatek</label>
                            <Calendar
                                id="start"
                                value={editedTask.start}
                                onChange={handleInputChange}
                                showIcon
                                dateFormat="yy-mm-dd"
                                showTime={false}
                                showWeek
                                placeholder="yyyy-mm-dd"
                                className="w-full"
                                locale="pl"
                            />
                        </div>
                        <div className="col-6 p-field mb-2">
                            <label htmlFor="progress">Koniec</label>
                            <Calendar
                                id="end"
                                value={editedTask.end}
                                onChange={handleInputChange}
                                showIcon
                                dateFormat="yy-mm-dd"
                                showTime={false}
                                showWeek
                                placeholder="yyyy-mm-dd"
                                className="w-full"
                                locale="pl"
                            />
                        </div>
                    </div>
                    <div className="p-fluid grid align-items-center">
                        <div className="col-6 p-field mb-2">
                            <label htmlFor="workHours">Roboczogodziny</label>
                            <InputNumber id="workHours" value={editedTask.workHours} onValueChange={handleInputChange} locale="de-DE" />
                        </div>

                        <div className="col-6 p-field mb-2">
                            <label htmlFor="progress">Progres</label>
                            <InputText
                                disabled={!(!isLoading && user && ((user._id === editedTask.user) || (user.type === "admin" || user.type === "manager")))}
                                id="progress"
                                value={editedTask.progress}
                                onChange={handleInputChange}
                            />
                            <Slider
                                name="progress"
                                value={editedTask.progress}
                                disabled={!(!isLoading && user && ((user._id === editedTask.user) || (user.type === "admin" || user.type === "manager")))}
                                onChange={handleInputChange}
                            />
                        </div>
                    </div>
                    <Divider align="left">
                        <span className="p-tag">Powiadomienie</span>
                    </Divider>
                    <div className="p-fluid grid align-items-center">
                        <div className="col-6">
                            <label htmlFor="startReminder" className="block mb-2">
                                Rozpoczęcie (dni przed)
                            </label>
                            <InputNumber
                                id="startReminder"
                                value={editedTask?.reminders?.startReminder || 0}
                                onValueChange={(e) => setEditedTask({ ...editedTask, reminders: { ...editedTask.reminders, startReminder: e.value || 0 } })}
                                min={0}
                                max={30}
                                showButtons
                            />
                        </div>

                        <div className="col-6">
                            <label htmlFor="endReminder" className="block mb-2">
                                Zakończenie (dni przed)
                            </label>
                            <InputNumber
                                id="endReminder"
                                value={editedTask?.reminders?.endReminder || 0}
                                onValueChange={(e) => setEditedTask({ ...editedTask, reminders: { ...editedTask.reminders, endReminder: e.value || 0 } })}
                                min={0}
                                max={30}
                                showButtons
                            />
                        </div>
                    </div>
                </div>
                <div className="col-12 md:col-6">
                    {!isLoading && user && (user.type === "admin" || user.type === "manager") && (
                        <>
                            <div className="p-field mb-2 w-full">
                                <UserDropdown
                                    id="user"
                                    selectedUser={editedTask.users}
                                    onUserSelect={handleUserChange}
                                    usersData={usersData}
                                    institutionsData={institutionsData}
                                    className="w-full"
                                    floating={false}
                                />
                            </div>
                            <div className="p-field mb-2 w-full">
                                <TasksDropdown
                                    TasksProject={editedTask.project}
                                    enabled={visible && !!editedTask.project}
                                    className="w-full"
                                    selectedTask={editedTask.dependencies ? editedTask.dependencies[0] : "brak"}
                                    editedTask={editedTask}
                                    onTaskSelect={handleDependencyChange}
                                />
                            </div>
                            {editedTask.dependencies && editedTask.dependencies.length > 0 && editedTask.dependencies[0] && editedTask.dependencies[0] !== "brak" && editedTask.dependencies[0] !== "" && (
                                <div className="p-field mb-2 w-full">
                                    <label htmlFor="dependencyOffset">Przesunięcie od zadania nadrzędnego (dni)</label>
                                    <InputNumber
                                        id="dependencyOffset"
                                        value={dependencyOffset}
                                        onValueChange={(e) => handleOffsetChange(e.value || 0)}
                                        showButtons
                                        min={-365}
                                        max={365}
                                        suffix=" dni"
                                    />
                                    <small className="p-text-secondary">
                                        Ujemna wartość = rozpoczęcie przed końcem zadania nadrzędnego<br/>
                                        Dodatnia wartość = rozpoczęcie po końcu zadania nadrzędnego
                                    </small>
                                </div>
                            )}
                            <div className="p-field mb-2 w-full">
                                <LegendColorPicker onColorSelect={(e) => setEditedTask({ ...editedTask, bgcolor: e })} selectedColor={editedTask.bgcolor}/>
                            </div>
                        </>
                    )}
                </div>
                    </div>
                </TabPanel>
                <TabPanel header="Logi">
                    <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
                        {logsLoading ? (
                            <div>Ładowanie logów...</div>
                        ) : logs.length === 0 ? (
                            <div>Brak logów dla tego zadania.</div>
                        ) : (
                            <div className="p-fluid">
                                {logs.map((l) => (
                                    <div key={l._id || l.timestamp} style={{ borderBottom: '1px solid #eee', padding: '8px 0' }}>
                                        <div style={{ fontSize: '0.9rem', color: '#333' }}>
                                            <b>{l.action}</b> — {new Date(l.timestamp).toLocaleString()}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#666' }}>
                                            Użytkownik: {l.username || 'unknown'} ({l.userType || '-'})
                                        </div>
                                        <div style={{ marginTop: 6 }}>
                                            <details>
                                                <summary style={{ fontSize: '0.85rem' }}>Zmodyfikowane pola</summary>
                                                <div style={{ marginTop: 6 }}>
                                                    {(() => {
                                                        const changes = computeChanges(l.before, l.after);
                                                        if (!changes || changes.length === 0) return <div style={{ fontSize: '0.85rem' }}>Brak zmian</div>;
                                                        return (
                                                            <div style={{ fontSize: '0.85rem' }}>
                                                                {changes.map((c) => (
                                                                    <div key={c.key} style={{ marginBottom: 6 }}>
                                                                        <div style={{ fontWeight: '600' }}>{getFieldLabel(c.key)}</div>
                                                                        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                                                            <div style={{ color: '#a00' }}>Przed: <code style={{ whiteSpace: 'pre-wrap' }}>{typeof c.before === 'object' ? JSON.stringify(c.before) : String(c.before ?? '')}</code></div>
                                                                            <div style={{ color: '#0a0' }}>Po: <code style={{ whiteSpace: 'pre-wrap' }}>{typeof c.after === 'object' ? JSON.stringify(c.after) : String(c.after ?? '')}</code></div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </details>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </TabPanel>
            </TabView>
        </Dialog>
    );
};

export default TaskModal;